(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ncs-custom-request]';
  const OPEN_CLASS = 'ncs-modal-is-open';

  /**
   * Returns all currently focusable elements inside a container.
   */
  const getFocusableElements = (container) => {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled]):not([type="hidden"])',
          'textarea:not([disabled])',
          'select:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',')
      )
    ).filter((element) => {
      return (
        element.offsetParent !== null &&
        element.getAttribute('aria-hidden') !== 'true'
      );
    });
  };

  /**
   * Initializes one custom format request component.
   */
  const initializeComponent = (root) => {
    if (!root || root.dataset.ncsInitialized === 'true') return;

    const openButton = root.querySelector('[data-ncs-modal-open]');
    const modal = root.querySelector('[data-ncs-modal]');
    const modalPanel = modal?.querySelector('[data-ncs-modal-panel]');
    const closeButtons = modal?.querySelectorAll('[data-ncs-modal-close]');

    if (!openButton || !modal || !modalPanel) return;

    root.dataset.ncsInitialized = 'true';

    let previouslyFocusedElement = null;

    /*
     * Move the modal directly under body.
     * This prevents headers, review widgets, sticky carts,
     * transformed containers, and product sections from covering it.
     */
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    /**
     * Find the main product form associated with the current page.
     */
    const getProductForm = () => {
      const productSection =
        root.closest('.shopify-section') ||
        document.querySelector('[data-section-type="main-product"]') ||
        document;

      return (
        productSection.querySelector('form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]')
      );
    };

    /**
     * Close all custom dropdowns inside the modal.
     */
    const closeAllSelects = (exceptSelect = null) => {
      modal.querySelectorAll('[data-ncs-select]').forEach((select) => {
        if (select === exceptSelect) return;

        const trigger = select.querySelector('[data-ncs-select-trigger]');
        const menu = select.querySelector('[data-ncs-select-menu]');

        select.classList.remove('is-open');

        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
        }

        if (menu) {
          menu.hidden = true;
        }
      });
    };

    /**
     * Update the product variant information included in the request.
     */
    const updateSelectedVariant = () => {
      const productForm = getProductForm();

      if (!productForm) return;

      const variantDisplay = modal.querySelector(
        '[data-ncs-variant-display]'
      );

      const variantInput = modal.querySelector(
        '[data-ncs-variant-input]'
      );

      const variantIdInput = modal.querySelector(
        '[data-ncs-variant-id]'
      );

      const variantIdField = productForm.querySelector('[name="id"]');
      const selectedVariantId = variantIdField?.value || '';

      let selectedTitle = '';

      /*
       * Some Shopify themes use a visible variant select.
       */
      if (
        variantIdField &&
        variantIdField.tagName === 'SELECT' &&
        variantIdField.selectedOptions.length
      ) {
        selectedTitle =
          variantIdField.selectedOptions[0]?.textContent?.trim() || '';
      }

      /*
       * Read radio-button product options.
       */
      if (!selectedTitle) {
        const selectedRadioOptions = Array.from(
          productForm.querySelectorAll(
            'input[type="radio"][name^="options"]:checked'
          )
        )
          .map((field) => field.value?.trim())
          .filter(Boolean);

        if (selectedRadioOptions.length) {
          selectedTitle = selectedRadioOptions.join(' / ');
        }
      }

      /*
       * Read select-based product options.
       */
      if (!selectedTitle) {
        const selectedDropdownOptions = Array.from(
          productForm.querySelectorAll('select[name^="options"]')
        )
          .map((select) => {
            return (
              select.selectedOptions[0]?.textContent?.trim() ||
              select.value?.trim()
            );
          })
          .filter(Boolean);

        if (selectedDropdownOptions.length) {
          selectedTitle = selectedDropdownOptions.join(' / ');
        }
      }

      /*
       * Fallback for themes that expose the selected option as text.
       */
      if (!selectedTitle) {
        const selectedValueElements = Array.from(
          document.querySelectorAll(
            [
              '[data-selected-value]',
              '[data-option-value][aria-checked="true"]',
              '.product-form__input input:checked + label',
              'variant-picker input:checked + label'
            ].join(',')
          )
        )
          .map((element) => {
            return (
              element.dataset.selectedValue ||
              element.dataset.optionValue ||
              element.textContent?.trim()
            );
          })
          .filter(Boolean);

        if (selectedValueElements.length) {
          selectedTitle = [...new Set(selectedValueElements)].join(' / ');
        }
      }

      if (!selectedTitle) {
        selectedTitle = 'Current selection';
      }

      if (variantDisplay) {
        variantDisplay.textContent = `Selected option: ${selectedTitle}`;
      }

      if (variantInput) {
        variantInput.value = selectedTitle;
      }

      if (variantIdInput) {
        variantIdInput.value = selectedVariantId;
      }
    };

    /**
     * Open modal.
     */
    const openModal = () => {
      previouslyFocusedElement = document.activeElement;

      updateSelectedVariant();

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');

      document.documentElement.classList.add(OPEN_CLASS);
      document.body.classList.add(OPEN_CLASS);

      window.requestAnimationFrame(() => {
        const focusableElements = getFocusableElements(modal);

        if (focusableElements.length) {
          focusableElements[0].focus();
        } else {
          modalPanel.focus();
        }
      });
    };

    /**
     * Close modal.
     */
    const closeModal = () => {
      closeAllSelects();

      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');

      document.documentElement.classList.remove(OPEN_CLASS);
      document.body.classList.remove(OPEN_CLASS);

      if (
        previouslyFocusedElement &&
        typeof previouslyFocusedElement.focus === 'function'
      ) {
        previouslyFocusedElement.focus();
      }
    };

    /**
     * Initialize custom dropdowns.
     */
    const initializeCustomSelect = (select) => {
      const trigger = select.querySelector('[data-ncs-select-trigger]');
      const menu = select.querySelector('[data-ncs-select-menu]');
      const visibleLabel = select.querySelector('[data-ncs-select-label]');
      const errorMessage = select.querySelector(
        '[data-ncs-select-error]'
      );

      const options = Array.from(
        select.querySelectorAll('[role="option"]')
      );

      const selectType = select.dataset.ncsSelectType;

      let hiddenInput = null;

      if (selectType === 'format') {
        hiddenInput = modal.querySelector('[data-ncs-format-input]');
      }

      if (selectType === 'frame') {
        hiddenInput = modal.querySelector('[data-ncs-frame-input]');
      }

      if (
        !trigger ||
        !menu ||
        !visibleLabel ||
        !hiddenInput ||
        !options.length
      ) {
        return;
      }

      const openSelect = () => {
        closeAllSelects(select);

        select.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        menu.hidden = false;

        const selectedOption =
          options.find(
            (option) =>
              option.getAttribute('aria-selected') === 'true'
          ) || options[0];

        window.requestAnimationFrame(() => {
          selectedOption?.focus();
        });
      };

      const closeSelect = (returnFocus = false) => {
        select.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        menu.hidden = true;

        if (returnFocus) {
          trigger.focus();
        }
      };

      const chooseOption = (option) => {
        const value =
          option.dataset.value || option.textContent.trim();

        hiddenInput.value = value;
        visibleLabel.textContent = option.textContent.trim();

        options.forEach((item) => {
          item.setAttribute(
            'aria-selected',
            item === option ? 'true' : 'false'
          );
        });

        select.classList.remove('has-error');

        if (errorMessage) {
          errorMessage.hidden = true;
        }

        closeSelect(true);
      };

      trigger.addEventListener('click', () => {
        const isOpen = select.classList.contains('is-open');

        if (isOpen) {
          closeSelect();
        } else {
          openSelect();
        }
      });

      trigger.addEventListener('keydown', (event) => {
        if (
          event.key === 'ArrowDown' ||
          event.key === 'ArrowUp' ||
          event.key === 'Enter' ||
          event.key === ' '
        ) {
          event.preventDefault();
          openSelect();
        }
      });

      options.forEach((option, index) => {
        option.setAttribute('tabindex', '-1');

        option.addEventListener('click', () => {
          chooseOption(option);
        });

        option.addEventListener('keydown', (event) => {
          if (
            event.key === 'Enter' ||
            event.key === ' '
          ) {
            event.preventDefault();
            chooseOption(option);
            return;
          }

          if (event.key === 'ArrowDown') {
            event.preventDefault();

            const nextIndex = (index + 1) % options.length;
            options[nextIndex].focus();
            return;
          }

          if (event.key === 'ArrowUp') {
            event.preventDefault();

            const previousIndex =
              (index - 1 + options.length) % options.length;

            options[previousIndex].focus();
            return;
          }

          if (event.key === 'Home') {
            event.preventDefault();
            options[0].focus();
            return;
          }

          if (event.key === 'End') {
            event.preventDefault();
            options[options.length - 1].focus();
            return;
          }

          if (event.key === 'Escape') {
            event.preventDefault();
            closeSelect(true);
            return;
          }

          if (event.key === 'Tab') {
            closeSelect();
          }
        });
      });
    };

    modal
      .querySelectorAll('[data-ncs-select]')
      .forEach(initializeCustomSelect);

    /**
     * Validate custom required dropdowns before submission.
     */
    const requestForm = modal.querySelector('[data-ncs-request-form]');

    requestForm?.addEventListener('submit', (event) => {
      let firstInvalidTrigger = null;

      modal
        .querySelectorAll(
          '[data-ncs-select][data-required="true"]'
        )
        .forEach((select) => {
          const selectType = select.dataset.ncsSelectType;

          let hiddenInput = null;

          if (selectType === 'format') {
            hiddenInput = modal.querySelector(
              '[data-ncs-format-input]'
            );
          }

          if (selectType === 'frame') {
            hiddenInput = modal.querySelector(
              '[data-ncs-frame-input]'
            );
          }

          const trigger = select.querySelector(
            '[data-ncs-select-trigger]'
          );

          const errorMessage = select.querySelector(
            '[data-ncs-select-error]'
          );

          if (!hiddenInput?.value) {
            event.preventDefault();

            select.classList.add('has-error');

            if (errorMessage) {
              errorMessage.hidden = false;
            }

            if (!firstInvalidTrigger) {
              firstInvalidTrigger = trigger;
            }
          } else {
            select.classList.remove('has-error');

            if (errorMessage) {
              errorMessage.hidden = true;
            }
          }
        });

      if (firstInvalidTrigger) {
        firstInvalidTrigger.focus();
      }
    });

    /**
     * Open and close actions.
     */
    openButton.addEventListener('click', openModal);

    closeButtons?.forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    /*
     * Prevent clicks inside the panel from reaching the overlay.
     */
    modalPanel.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    /*
     * Close dropdowns when clicking elsewhere inside the modal.
     */
    modal.addEventListener('click', (event) => {
      if (!event.target.closest('[data-ncs-select]')) {
        closeAllSelects();
      }
    });

    /**
     * Keyboard controls and focus trap.
     */
    modal.addEventListener('keydown', (event) => {
      if (modal.hidden) return;

      if (event.key === 'Escape') {
        const openSelect = modal.querySelector(
          '[data-ncs-select].is-open'
        );

        if (openSelect) {
          closeAllSelects();
          openSelect
            .querySelector('[data-ncs-select-trigger]')
            ?.focus();

          event.preventDefault();
          return;
        }

        closeModal();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(modal);

      if (!focusableElements.length) {
        event.preventDefault();
        modalPanel.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    });

    /**
     * Monitor variant changes.
     */
    document.addEventListener('change', (event) => {
      const target = event.target;

      if (!(target instanceof Element)) return;

      if (
        target.matches(
          [
            'form[action*="/cart/add"] [name="id"]',
            'form[action*="/cart/add"] input[name^="options"]',
            'form[action*="/cart/add"] select[name^="options"]',
            'variant-picker input',
            'variant-picker select',
            'variant-selects input',
            'variant-selects select'
          ].join(',')
        )
      ) {
        window.setTimeout(updateSelectedVariant, 60);
      }
    });

    /*
     * Support themes that dispatch a custom variant-change event.
     */
    document.addEventListener('variant:change', () => {
      window.setTimeout(updateSelectedVariant, 60);
    });

    document.addEventListener('variantChange', () => {
      window.setTimeout(updateSelectedVariant, 60);
    });

    updateSelectedVariant();

    /**
     * Automatically reopen the modal after Shopify returns
     * a successful contact-form response.
     */
    if (modal.querySelector('[data-ncs-success-message]')) {
      openModal();

      window.requestAnimationFrame(() => {
        modal
          .querySelector('[data-ncs-success-message]')
          ?.focus();
      });
    }
  };

  /**
   * Initialize all components currently present on the page.
   */
  const initializeAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initializeComponent);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeAll();
    });
  } else {
    initializeAll();
  }

  /**
   * Reinitialize when Shopify reloads a section in the theme editor.
   */
  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });

  /**
   * Remove detached modal when its Shopify section is unloaded.
   */
  document.addEventListener('shopify:section:unload', (event) => {
    const productId = event.target
      ?.querySelector(ROOT_SELECTOR)
      ?.dataset.productId;

    if (!productId) return;

    const modal = document.getElementById(
      `ncs-custom-format-modal-${productId}`
    );

    modal?.remove();

    document.documentElement.classList.remove(OPEN_CLASS);
    document.body.classList.remove(OPEN_CLASS);
  });
})();